import copy from 'copy-to-clipboard';
import React, { FC } from 'react';

import { Chip } from '@sbu/components.chip';
import { useToasts } from '@sbu/contexts.toasts';
import { useTags, useTagSchemas } from '@sbu/hooks.tags';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@sumologic/yama-ds-components';
import { CopyIcon, LinkIcon } from '@sumologic/yama-ds-icons';

export type TagChipSize = 'small' | 'medium';

export interface TagChipProps {
  contentTypes?: string[];
  disabled?: boolean;
  editable?: boolean;
  tag: string;
  onClick?: (tag: string) => void;
  removeChip?: (tag: string) => void;
}

interface ChipMenuItem {
  href?: string;
  icon: React.ReactNode;
  label: string;
  onClick?: any;
}

export const TagChip: FC<React.PropsWithChildren<TagChipProps>> = ({
  contentTypes,
  disabled = false,
  editable = false,
  tag,
  onClick,
  removeChip,
}) => {
  const { tagSchemas } = useTagSchemas();
  const { deserializeTag } = useTags(tagSchemas, contentTypes);
  const { label, link, value, valueLabel } = deserializeTag(tag);
  const { addSuccess } = useToasts();

  const linkMenuItem: ChipMenuItem = {
    href: link || undefined,
    icon: <LinkIcon />,
    label: 'Open the link',
  };

  const copyTagMenuItem: ChipMenuItem = {
    icon: <CopyIcon />,
    label: 'Copy the raw Tag',
    onClick: () => {
      copy(tag);

      addSuccess('The raw tag is copied to the clipboard.');
    },
  };

  const menuItems: ChipMenuItem[] = link
    ? [linkMenuItem, copyTagMenuItem]
    : [copyTagMenuItem];

  const createChipMenuItem = (chipMenuItem: ChipMenuItem) => {
    const { icon, label, href, onClick } = chipMenuItem;

    return (
      <MenuItem
        component={href ? 'a' : 'div'}
        onClick={onClick}
        href={href}
        rel={href ? 'noopener noreferrer' : undefined}
        spacing={1}
        target={href ? '_blank' : undefined}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />
      </MenuItem>
    );
  };

  return (
    <Chip
      label={valueLabel ? `${value} - ${valueLabel}` : value}
      DeleteButtonProps={{
        'aria-label': `Remove ${tag}`,
        'data-sumo-test': 'tag-chip-remove',
        title: `Remove ${tag}`,
      }}
      menuItems={menuItems}
      onDelete={
        editable && !disabled
          ? (event) => {
              event.stopPropagation();

              if (removeChip) {
                removeChip(tag);
              }
            }
          : undefined
      }
      onLabelClick={
        onClick
          ? (event) => {
              event.stopPropagation();

              onClick(tag);
            }
          : undefined
      }
      prefix={label}
      renderMenuItem={(chipMenuItem: ChipMenuItem) => {
        return createChipMenuItem(chipMenuItem);
      }}
      StackProps={{
        'data-sumo-test': 'tag-chip-container',
      }}
    />
  );
};
